import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import cns from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { CheckSmallCircleIcon, ExclamationmarkSmallCircleIcon, PenIcon } from '@zep/icons';
import { Accordion, AccordionButton, AccordionPanel, AccordionText, Input, TextField } from '@zep/ui';
import { StoreCategoryString, StoreImageType } from '../../../../consts';
import { useStoreLocalizeSetForm } from '../../../../hooks/useStoreForms';
import { AllLanguageType, StoreUploadLocalizeData } from '../../../../types/StoreTypes';
import { TextEditorExposeType } from '../../common/TextEditor';
import UploadImagesGrid from './UploadImagesGrid';
import S from './UploadLocalizeFormAccordion.module.scss';
import UploadThumbnailCard from './UploadThumbnailCard';
const initialUploadForm: StoreUploadLocalizeData = {
  name: '',
  shortDescription: '',
  description: '',
  thumbnailImageKey: '',
  previewImageKeyList: []
};
const TextEditor = dynamic(() => import('../../../../components/service/common/TextEditor'), {
  ssr: false
});

/**
 * 언어별 상세 입력 폼 컴포넌트
 * @param props
 * @constructor
 */
const UploadLocalizeFormAccordion = (props: AssetUploadAccordionProps) => {
  const {
    localeId,
    dataSetIndex,
    isEditBlocked,
    onCreateFormData,
    onFormDirty
  } = props;
  const router = useRouter();
  const {
    t
  } = useTranslation(['common', 'store']);
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(dataSetIndex === 0);
  const uploadType: string = typeof router.query.type === 'string' ? router.query.type : StoreCategoryString.map;
  const isAssetType = router.query.type === StoreCategoryString.map || uploadType === StoreCategoryString.object;
  const textEditorRef = useRef<TextEditorExposeType>(null);
  const localizeSetForm = useStoreLocalizeSetForm(uploadType, initialUploadForm);
  const {
    register,
    getValues,
    setValue,
    control,
    formState: {
      errors,
      isDirty,
      isValid
    }
  } = localizeSetForm;
  const typeLabelStringKey = useMemo(() => {
    switch (uploadType) {
      case StoreCategoryString.object:
        return t('store:store_asset_object_set');
      case StoreCategoryString.app:
        return t('store:store_asset_app');
      case StoreCategoryString.game:
        return t('store:store_asset_game');
    }
    return '';
  }, [t, uploadType]);
  const descriptionPlaceHolder = useMemo(() => {
    switch (uploadType) {
      case StoreCategoryString.object:
        return t('store:store_object_description');
      case StoreCategoryString.app:
        return t('store:store_app_description');
      case StoreCategoryString.game:
        return t('store:store_game_description');
    }
    return '';
  }, [t, uploadType]);
  useEffect(() => {
    onFormDirty(localeId, isDirty);
  }, [localeId, isDirty]);
  useEffectOnce(() => {
    onCreateFormData(localeId, localizeSetForm);
    setIsMounted(true);
  });
  return <Accordion className={S.accordion} isOpen={isOpen} data-sentry-element="Accordion" data-sentry-component="UploadLocalizeFormAccordion" data-sentry-source-file="UploadLocalizeFormAccordion.tsx">
      <AccordionButton onClick={() => {
      setIsOpen(!isOpen);
    }} data-sentry-element="AccordionButton" data-sentry-source-file="UploadLocalizeFormAccordion.tsx">
        <strong>{t(`common:language.${localeId as AllLanguageType}`)}</strong>
        {isOpen && !isValid &&
      // 아코디언이 열려있고 폼의 정보가 유효하지 않은 경우: 작성중
      <AccordionText type="info">
            <PenIcon width={16} height={16} aria-hidden />
            {t('store:store_localize_status_writing')}
          </AccordionText>}
        {!isOpen && !isValid &&
      // 아코디언이 닫혀있고 폼의 정보가 유효하지 않은 경우: 작성되지 않은 정보가 있습니다.
      <AccordionText type="danger">
            <ExclamationmarkSmallCircleIcon width={16} height={16} aria-hidden />
            {t('store:store_writing_it_yet')}
          </AccordionText>}
        {isValid &&
      // 폼의 정보가 유효한 경우: 작성완료
      <AccordionText type="success">
            <CheckSmallCircleIcon width={16} height={16} aria-hidden />
            {t('store:store_localize_status_complete')}
          </AccordionText>}
      </AccordionButton>

      <AccordionPanel className={S.upload_form} data-sentry-element="AccordionPanel" data-sentry-source-file="UploadLocalizeFormAccordion.tsx">
        {/* 썸네일 이미지 영역 */}
        {isAssetType && <div className={cns([S.upload_form_row, S.half_width])}>
            <label>
              {t('store:store_something_thumbnail', {
            x: typeLabelStringKey
          })}
              <small>{t('store:store_recommended_size')}</small>
            </label>
            <Controller control={control} name="thumbnailImageKey" render={({
          field: {
            value,
            onChange
          }
        }) => <UploadThumbnailCard thumbnail={value ?? ''} thumbnailKey={localeId} onThumbnailUpload={newThumbnail => onChange(newThumbnail)} disabled={false} maxWidth={230} aspectRatio="230/150" />} />

            {!!errors.thumbnailImageKey && <div className={S.error_text}>
                {errors.thumbnailImageKey?.message}
              </div>}
          </div>}

        {/* 이름 영역 */}
        <div className={cns([S.upload_form_row, S.half_width])}>
          <TextField error={!!errors.name} label={t('store:store_something_name', {
          x: typeLabelStringKey
        })} helperText={errors.name?.message} data-sentry-element="TextField" data-sentry-source-file="UploadLocalizeFormAccordion.tsx">
            <Input isFullWidth {...register('name')} maxLength={30} placeholder={t('store:store_enter_something_name', {
            type: typeLabelStringKey
          })} error={!!errors.name} data-sentry-element="Input" data-sentry-source-file="UploadLocalizeFormAccordion.tsx" />
          </TextField>
        </div>

        {/* 한줄 설명 영역 */}
        {!isAssetType && <div className={cns([S.upload_form_row, S.half_width])}>
            <TextField error={!!errors.shortDescription} label={t('store:store_something_description', {
          x: typeLabelStringKey
        })} helperText={errors.shortDescription?.message}>
              <Input isFullWidth {...register('shortDescription')} maxLength={100} placeholder={t('store:store_enter_description', {
            x: typeLabelStringKey
          })} error={!!errors.shortDescription} />
            </TextField>
          </div>}

        {/* 프리뷰 이미지 영역 */}
        {uploadType !== StoreCategoryString.object && <div className={S.upload_form_row}>
            <label>
              {t('store:store_something_preview_img', {
            x: typeLabelStringKey
          })}
              <small>{t('store:store_recommended_size_max5')}</small>
            </label>
            <Controller control={control} name={'previewImageKeyList'} render={({
          field: {
            value,
            onChange
          }
        }) => <UploadImagesGrid gridKey={localeId} assetImageKeyList={value ?? []} onFilesAdd={imageKeys => onChange([...(value ?? []), ...imageKeys])} onFileRemove={index => {
          value && value?.splice(index, 1);
          onChange(value);
        }} imageType={StoreImageType.preview} fileMaxCount={5} disabled={false} />} />
            {errors.previewImageKeyList && <div className={S.error_text}>
                {errors.previewImageKeyList.message}
              </div>}
          </div>}

        {/* 소개 영역 */}
        {isMounted &&
      // 기존 데이터 로딩 후 텍스트 에디터에 값을 세팅하기 위해 마운트가 끝난 후 element 생성
      <div className={S.upload_form_row}>
            <label>
              {isAssetType ? t('store:store_something_introduce', {
            x: typeLabelStringKey
          }) : t('store:store_something_how_use', {
            x: typeLabelStringKey
          })}
            </label>
            <TextEditor initialText={getValues('description')} ref={textEditorRef} disabled={isEditBlocked} locale={router.locale} onTextUpdate={text => {
          setValue('description', text, {
            shouldValidate: true
          });
        }} placeholder={descriptionPlaceHolder} />
          </div>}
      </AccordionPanel>
    </Accordion>;
};
export default UploadLocalizeFormAccordion;
type AssetUploadAccordionProps = {
  localeId: string;
  dataSetIndex: number;
  isEditBlocked: boolean;
  onCreateFormData: (localeId: string, form: UseFormReturn<StoreUploadLocalizeData>) => void;
  onFormDirty: (localeId: string, value: boolean) => void;
};